import dayjs from "dayjs";
import { type PackagingInformationRefinedEntryDTO, type WarehouseStorageDataDTO } from "../../dto/ProductDataDTO";
import {
  type InStockEntryData,
  type PackagingInformationEntry,
  type WarehouseStockData,
} from "../../models/ProductData";
import { getPackageCode } from "../../utils/cartHelper";

import { groupByFn, isNonNull } from "~/utils/reducers";

export interface AvailabilityInfoDTO {
  code: string;
  sapMaterialNumber: string;
  packagingInformationRefined: PackagingInformationRefinedEntryDTO[] | null;
  isFreshPnaCall: boolean | null;
}

export interface AvailabilityInfo {
  code: string;
  sapMaterialNumber: string;

  hasPackaging: boolean;
  packagingInformation: PackagingInformationEntry[];
  isFreshPnaCall: boolean;
}

export function transformAvailability(dto: AvailabilityInfoDTO): AvailabilityInfo {
  const packagingInfo = (dto.packagingInformationRefined || []).map((entry) => {
    const global = transformWarehouse(entry.global);
    const warehouses = entry.warehouses.map(transformWarehouse);
    return {
      packCode: getPackageCode(entry.code),
      packSize: entry.packsize,
      primaryWarehouseCode: entry.primaryWarehouseCode,
      primaryWarehouseName: entry.primaryWarehouseName,
      replenishmentLeadTimeDate: entry.replenishmentLeadTimeDate ? new Date(entry.replenishmentLeadTimeDate) : null,
      preferredPackQuantity: entry.prefPackQuantity ?? 0,
      global,
      warehouses,
      totalWarehouse: combineWarehouses([...warehouses, global]),
      salesStock: entry.salesStock,
    };
  }) as PackagingInformationEntry[];

  packagingInfo.sort((a, b) => a.packSize - b.packSize);

  return {
    code: dto.code,
    sapMaterialNumber: dto.sapMaterialNumber,

    hasPackaging: packagingInfo.length > 0,
    packagingInformation: packagingInfo, //
    isFreshPnaCall: dto.isFreshPnaCall || false,
  };
}

const transformWarehouse = (dto: WarehouseStorageDataDTO): WarehouseStockData => {
  const entryWithoutDate = (dto.inProduction ?? []).filter((entry) => !entry.date)[0];
  const inStock: InStockEntryData[] = [];
  if (entryWithoutDate) {
    inStock.push({
      quantity: entryWithoutDate.amount ?? 0,
    });
  }
  if (dto.inStock) {
    const list = Array.isArray(dto.inStock) ? dto.inStock : [dto.inStock];
    list.forEach((inStockItem) => {
      inStock.push({
        quantity: inStockItem.amount ?? 0,
        date: inStockItem.date ? new Date(inStockItem.date) : undefined,
      });
    });
  }

  return {
    code: dto.code ?? "",
    name: dto.name ?? "",
    inStock,
    inClarification: dto.inClarification ?? 0,
    inProduction: (dto.inProduction ?? [])
      .map((entry, index) => {
        if (!entry.date) {
          if (import.meta.env.DEV) {
            console.warn(`Date missing in entry nr ${index + 1} from warehouse ${dto.name ?? "global"} `);
          }
          return null;
        }
        return {
          quantity: entry.amount,
          date: new Date(entry.date),
        };
      })
      .filter(isNonNull),
  };
};

function combineWarehouses(warehouses: WarehouseStockData[]): WarehouseStockData {
  const primaryWarehouse = warehouses[0];

  return {
    ...primaryWarehouse,
    inStock: combineStockEntries(warehouses.flatMap((warehouse) => warehouse.inStock)),
    inProduction: combineStockEntries(warehouses.flatMap((warehouse) => warehouse.inProduction)),
    inClarification: warehouses.reduce((acc, warehouse) => acc + warehouse.inClarification, 0),
  };
}

function combineStockEntries<T extends InStockEntryData>(stockEntries: T[]): T[] {
  const grouped = groupByFn(stockEntries, (entry) => dayjs(entry.date).format("YYYY-MM-DD"));
  return Object.values(grouped)
    .map((sameDayEntries: T[]) => ({
      date: sameDayEntries[0].date,
      quantity: sameDayEntries.reduce((acc, entry) => acc + entry.quantity, 0),
    }))
    .sort((e1, e2) => {
      if (e1.date === undefined) {
        return -1;
      }
      if (e2.date === undefined) {
        return -1;
      }
      return e1.date.getTime() - e2.date.getTime();
    }) as T[];
}
